import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Loader2,
  Database,
  FileText,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Info
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const MAINTENANCE_OPERATIONS = [
  {
    id: 'resync-doc-content',
    name: 'Re-sync Document Content',
    description: 'Re-fetch the latest content from Google Docs. Use this when documents have been edited in Google and you want to update the stored content and extract new references.',
    icon: RefreshCw,
    endpoint: '/integrations/resync-doc-content',
    method: 'POST',
    confirmMessage: 'This will fetch fresh content from Google for all documents. Use this after editing documents in Google Docs to update the stored content and extract new references.',
  },
  {
    id: 'backfill-doc-references',
    name: 'Backfill Document References',
    description: 'Extract cross-references (GitHub PRs, Linear issues, etc.) from existing stored Google Docs content and create edges in the graph.',
    icon: FileText,
    endpoint: '/integrations/backfill-doc-references',
    method: 'POST',
    confirmMessage: 'This will process all Google Docs in your organization to extract references from stored content. Run "Re-sync Document Content" first if you edited docs in Google.',
  },
  {
    id: 'fix-all-metadata',
    name: 'Fix All Integration Metadata',
    description: 'Update metadata for all integration connections (Slack, Linear, GitHub) by syncing project sources from the database.',
    icon: RefreshCw,
    endpoint: '/integrations/fix-all-metadata',
    method: 'POST',
    confirmMessage: 'This will update metadata for ALL integration connections. Use this if syncs are returning 0 records due to missing metadata.',
  },
  {
    id: 'fix-linear-metadata',
    name: 'Fix Linear Metadata',
    description: 'Fix metadata for Linear connections by setting projectTeams based on existing project sources.',
    icon: Database,
    endpoint: '/integrations/linear/fix-metadata',
    method: 'POST',
    confirmMessage: 'This will update metadata for all Linear connections in your organization.',
  },
];

export default function DataMaintenancePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});

  React.useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/settings');
    }
  }, [user, navigate]);

  const executeOperation = async (operation) => {
    // Confirm with user
    if (!window.confirm(operation.confirmMessage)) {
      return;
    }

    const opId = operation.id;
    setLoading(prev => ({ ...prev, [opId]: true }));
    setResults(prev => ({ ...prev, [opId]: null }));

    try {
      const response = await api({
        method: operation.method,
        url: operation.endpoint,
      });

      setResults(prev => ({
        ...prev,
        [opId]: {
          success: true,
          data: response.data,
        },
      }));

      // Show success message
      const message = response.data.message || 'Operation completed successfully';
      toast.success(message);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Operation failed';

      setResults(prev => ({
        ...prev,
        [opId]: {
          success: false,
          error: errorMessage,
        },
      }));

      toast.error(`Operation failed: ${errorMessage}`);
    } finally {
      setLoading(prev => ({ ...prev, [opId]: false }));
    }
  };

  const renderOperationCard = (operation) => {
    const Icon = operation.icon;
    const opId = operation.id;
    const isLoading = loading[opId];
    const result = results[opId];

    return (
      <Card key={opId} className="p-6 border-l-4 border-l-primary">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{operation.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {operation.description}
              </p>
            </div>
          </div>

          {/* Result Display */}
          {result && (
            <div
              className={`p-4 rounded-lg border ${
                result.success
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-start gap-2">
                {result.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p
                    className={`text-sm font-medium ${
                      result.success ? 'text-green-900' : 'text-red-900'
                    }`}
                  >
                    {result.success ? 'Success' : 'Failed'}
                  </p>
                  {result.success && result.data && (
                    <div className="mt-2 text-sm text-green-800">
                      {result.data.message && (
                        <p className="font-medium">{result.data.message}</p>
                      )}
                      {result.data.stats && (
                        <div className="mt-2 space-y-1">
                          {Object.entries(result.data.stats).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="capitalize">
                                {key.replace(/_/g, ' ')}:
                              </span>
                              <span className="font-mono">{value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {result.data.updated !== undefined && (
                        <p className="mt-1">
                          Updated: <span className="font-mono">{result.data.updated}</span>
                        </p>
                      )}
                    </div>
                  )}
                  {result.error && (
                    <p className="mt-1 text-sm text-red-800">{result.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Button */}
          <div className="flex justify-end">
            <Button
              onClick={() => executeOperation(operation)}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon className="h-4 w-4 mr-2" />
                  Run Operation
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Data Maintenance</h1>
            <p className="text-muted-foreground mt-1">
              Run data maintenance and backfill operations
            </p>
          </div>
          <Badge variant="destructive">Admin Only</Badge>
        </div>

        {/* Info Banner */}
        <Card className="p-4 bg-muted/50 border-blue-200">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Important Notes</p>
              <ul className="text-muted-foreground mt-1 space-y-1 list-disc list-inside">
                <li>These operations may take several minutes to complete</li>
                <li>Only run these operations if instructed or troubleshooting issues</li>
                <li>Operations are idempotent and safe to run multiple times</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Operation Cards */}
        <div className="space-y-4">
          {MAINTENANCE_OPERATIONS.map(renderOperationCard)}
        </div>
      </div>
    </AppShell>
  );
}
